import React, { useEffect, useState } from 'react';
import pickBy from 'lodash/pickBy';
import classNames from 'classnames';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../../util/search';
import { parse, stringify } from '../../../util/urlHelpers';
import { createResourceLocatorString, matchPathname, pathByRouteName } from '../../../util/routes';
import {
  Button,
  PrimaryButton,
  LimitedAccessBanner,
  LinkedLogo,
  Modal,
  ModalMissingInformation,
} from '../../../components';
import { getSearchPageResourceLocatorStringParams } from '../../SearchPage/SearchPage.shared';
import {
  applySearchPageDateQueryParams,
  mergeTopbarSearchIntoQuery,
  parseDateRangeForSearchForm,
} from '../../../util/buildListingSearchParams';

import MenuIcon from './MenuIcon';
import SearchIcon from './SearchIcon';
import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';
import SupportModal from '../SupportModal/SupportModal';
import useStripeStatus from '../../ProviderPage/hooks/useStripeStatus';
import { createCoachPayoutAccountOnboardingLinkApi } from '../../../util/api';
import { isPopupOnCooldown, dismissPopupWithCooldown } from '../../../util/popupCooldown';

import css from './Topbar.module.css';

const MAX_MOBILE_SCREEN_WIDTH = 1024;

const SEARCH_DISPLAY_ALWAYS = 'always';
const SEARCH_DISPLAY_NOT_LANDING_PAGE = 'notLandingPage';
const SEARCH_DISPLAY_ONLY_SEARCH_PAGE = 'onlySearchPage';

const redirectToURLWithModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  // Note: sort order is stable in JS
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};
// Returns links in order where primary links are returned first
const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.sort(compareGroups);
};

// Resolves in-app links against route configuration
const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.map(linkConfig => {
    const { type, href } = linkConfig;
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      // Internal link
      try {
        const testURL = new URL('http://my.marketplace.com' + href);
        const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
        if (matchedRoutes.length > 0) {
          const found = matchedRoutes[0];
          const to = { search: testURL.search, hash: testURL.hash };
          return {
            ...linkConfig,
            route: {
              name: found.route?.name,
              params: found.params,
              to,
            },
          };
        }
      } catch (e) {
        return linkConfig;
      }
    }
    return linkConfig;
  });
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName ? cmsPageName : inboxPageName ? inboxPageName : `${found.route?.name}`;
  }
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return (
    <div className={classes}>
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  );
};

const TopbarComponent = props => {
  const {
    className,
    rootClassName,
    desktopClassName,
    mobileRootClassName,
    mobileClassName,
    isAuthenticated,
    isLoggedInAs,
    authScopes = [],
    authInProgress,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    stripeAccount,
    currentPage,
    notificationCount = 0,
    intl,
    history,
    location,
    onManageDisableScrolling,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    showGenericError,
    config,
    routeConfiguration,
    onBecomeCoach,
  } = props;

  const [isBecomeCoachModalOpen, setIsBecomeCoachModalOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  // Backed by localStorage (see popupCooldown.js), not just component state
  // -- otherwise dismissing this and opening a new tab/window (a fresh page
  // load) reset the flag and popped the modal right back up, since a plain
  // useState(false) doesn't survive across page loads.
  const [isPayoutReconnectDismissed, setIsPayoutReconnectDismissed] = useState(() =>
    isPopupOnCooldown('payoutReconnect')
  );
  const [payoutConnecting, setPayoutConnecting] = useState(false);
  const [payoutConnectError, setPayoutConnectError] = useState('');

  const dismissPayoutReconnectModal = () => {
    dismissPopupWithCooldown('payoutReconnect');
    setIsPayoutReconnectDismissed(true);
  };

  const isCoach = currentUser?.attributes?.profile?.publicData?.coach === true;
  const { stripeConnected, statusLoaded: payoutStatusLoaded } = useStripeStatus();
  // Condition-driven rather than a one-time "seen it" flag, so it keeps
  // showing until the coach actually reconnects (bounded by the 30-minute
  // cooldown above once dismissed), and naturally covers every coach in
  // this state, not just the ones known about today.
  // isAuthenticated only reflects the Sharetribe cookie, which native
  // sessions never carry -- this silently never showed the popup to any
  // native-session coach. currentUser is populated for both auth paths.
  const showPayoutReconnectModal =
    !!currentUser && isCoach && payoutStatusLoaded && !stripeConnected && !isPayoutReconnectDismissed;

  const handleConnectPayout = async () => {
    setPayoutConnecting(true);
    setPayoutConnectError('');
    try {
      const response = await createCoachPayoutAccountOnboardingLinkApi();
      if (response?.url) {
        window.location.href = response.url;
      } else {
        setPayoutConnectError('Could not start payout setup.');
        setPayoutConnecting(false);
      }
    } catch (e) {
      setPayoutConnectError(e?.error || 'Could not start payout setup.');
      setPayoutConnecting(false);
    }
  };

  const pushSearchPageParams = searchParams => {
    const { history, location, routeConfiguration } = props;
    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, searchParams)
    );
  };

  const handleSubmit = values => {
    const { currentSearchParams, config } = props;

    const topbarSearchParams = () => {
      // If the form provides a location, use it.
      if (values?.location) {
        const { search, selectedPlace } = values.location;
        const { origin, bounds } = selectedPlace;
        const originMaybe = isOriginInUse(config) ? { origin } : {};

        return {
          ...originMaybe,
          address: search,
          bounds,
        };
      }

      // Otherwise, just return all values (like keywords, pub_category, dates, etc.)
      return values;
    };
    const submittedParams = topbarSearchParams();
    const searchParams = mergeTopbarSearchIntoQuery(currentSearchParams, submittedParams);
    pushSearchPageParams(searchParams);
  };

  const handleLogout = () => {
    const { onLogout, history, routeConfiguration } = props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      console.log('logged out'); // eslint-disable-line
    });
  };

  const {
    mobilemenu,
    mobilesearch,
    keywords,
    address,
    origin,
    bounds,
    pub_categoryLevel1,
    pub_categoryLevel2,
    dates,
    startDate,
    endDate,
    date,
  } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  // Custom links are sorted so that group="primary" are always at the beginning of the list.
  const sortedCustomLinks = sortCustomLinks(config.topbar?.customLinks);
  const customLinks = getResolvedCustomLinks(sortedCustomLinks, routeConfiguration);
  const resolvedCurrentPage = currentPage || getResolvedCurrentPage(location, routeConfiguration);

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';
  const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';

  const mobileMenu = (
    <TopbarMobileMenu
      isAuthenticated={isAuthenticated}
      currentUserHasListings={currentUserHasListings}
      currentUser={currentUser}
      onLogout={handleLogout}
      notificationCount={notificationCount}
      currentPage={resolvedCurrentPage}
      customLinks={customLinks}
      onToggleBecomeCoachModal={setIsBecomeCoachModalOpen}
    />
  );

  const topbarSearcInitialValues = () => {
    const dateRange = parseDateRangeForSearchForm({ dates, startDate, endDate, date });

    return {
      keywords,
      category: pub_categoryLevel1,
      subcategory: pub_categoryLevel2,
      dateRange,
      location: (isOriginInUse(config) ? address && origin && bounds : address && bounds)
        ? {
          search: address,
          selectedPlace: { address, origin, bounds },
        }
        : null,
    };
  };
  const initialSearchFormValues = topbarSearcInitialValues();
  const searchFormKey = `topbar-search-${dates || ''}-${startDate || ''}-${endDate || ''}-${pub_categoryLevel1 || ''}-${pub_categoryLevel2 || ''}-${keywords || ''}`;

  const isSearchResultsPage =
    resolvedCurrentPage === 'SearchPage' || resolvedCurrentPage === 'SearchPageWithListingType';

  const handleDateRangeChange = dateRange => {
    if (!isSearchResultsPage) {
      return;
    }
    const { currentSearchParams } = props;
    const searchParams = applySearchPageDateQueryParams(currentSearchParams, dateRange);
    pushSearchPageParams(searchParams);
  };

  const classes = classNames(rootClassName || css.root, className);

  const { display: searchFormDisplay = SEARCH_DISPLAY_ALWAYS } = config?.topbar?.searchBar || {};

  // Search form is shown conditionally depending on configuration and
  // the current page.
  const showSearchOnAllPages = searchFormDisplay === SEARCH_DISPLAY_ALWAYS;
  const showSearchOnSearchPage =
    searchFormDisplay === SEARCH_DISPLAY_ONLY_SEARCH_PAGE &&
    ['SearchPage', 'SearchPageWithListingType'].includes(resolvedCurrentPage);
  const showSearchNotOnLandingPage =
    searchFormDisplay === SEARCH_DISPLAY_NOT_LANDING_PAGE && resolvedCurrentPage !== 'LandingPage';

  const showSearchForm =
    showSearchOnAllPages || showSearchOnSearchPage || showSearchNotOnLandingPage;

  const mobileSearchButtonMaybe = showSearchForm ? (
    <Button
      rootClassName={css.searchMenu}
      onClick={() => redirectToURLWithModalState(history, location, 'mobilesearch')}
      title={intl.formatMessage({ id: 'Topbar.searchIcon' })}
    >
      <SearchIcon className={css.searchMenuIcon} />
    </Button>
  ) : (
    <div className={css.searchMenu} />
  );




  return (
    <div className={classes}>
      <LimitedAccessBanner
        isAuthenticated={isAuthenticated}
        isLoggedInAs={isLoggedInAs}
        authScopes={authScopes}
        currentUser={currentUser}
        onLogout={handleLogout}
        currentPage={resolvedCurrentPage}
      />
      <div className={classNames(mobileRootClassName || css.container, mobileClassName)}>
        {currentUser ? (
          <Button
            rootClassName={css.supportMenu}
            onClick={() => setIsSupportModalOpen(true)}
            title="Support"
          >
            <span className={css.supportMenuIcon}>?</span>
          </Button>
        ) : null}
        <Button
          rootClassName={css.menu}
          onClick={() => redirectToURLWithModalState(history, location, 'mobilemenu')}
          title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
        >
          <MenuIcon className={css.menuIcon} />
          {notificationDot}
        </Button>

        <LinkedLogo
          layout={'mobile'}
          alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
          linkToExternalSite={config?.topbar?.logoLink}
        />
        {mobileSearchButtonMaybe}
      </div>
      <div className={css.desktop}>
        <TopbarDesktop
          className={desktopClassName}
          currentUserHasListings={currentUserHasListings}
          currentUser={currentUser}
          currentPage={resolvedCurrentPage}
          initialSearchFormValues={initialSearchFormValues}
          intl={intl}
          isAuthenticated={isAuthenticated}
          notificationCount={notificationCount}
          onLogout={handleLogout}
          onSearchSubmit={handleSubmit}
          onDateRangeChange={handleDateRangeChange}
          searchFormKey={searchFormKey}
          config={config}
          customLinks={customLinks}
          showSearchForm={showSearchForm}
          onToggleBecomeCoachModal={setIsBecomeCoachModalOpen}
          onOpenSupportModal={() => setIsSupportModalOpen(true)}
        />
      </div>
      <Modal
        id="TopbarMobileMenu"
        containerClassName={css.modalContainer}
        isOpen={isMobileMenuOpen}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilemenu')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        {authInProgress ? null : mobileMenu}
      </Modal>
      <Modal
        id="TopbarMobileSearch"
        scrollLayerClassName={css.mobileSearchModalScrollLayer}
        containerClassName={css.modalContainerSearchForm}
        isOpen={isMobileSearchOpen}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilesearch')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.searchContainer}>
          <TopbarSearchForm
            key={searchFormKey}
            onSubmit={handleSubmit}
            onDateRangeChange={handleDateRangeChange}
            initialValues={initialSearchFormValues}
            isMobile
            appConfig={config}
          />
          <p className={css.mobileHelp}>
            <FormattedMessage id="Topbar.mobileSearchHelp" />
          </p>
        </div>
      </Modal>
      <ModalMissingInformation
        id="MissingInformationReminder"
        containerClassName={css.missingInformationModal}
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        location={location}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
      />

      <Modal
        id="SupportModal"
        containerClassName={css.supportModalContainer}
        contentClassName={css.supportModalContent}
        iconOnlyClose
        isOpen={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <SupportModal currentUser={currentUser} />
      </Modal>

      <Modal
        id="BecomeCoachModal"
        containerClassName={css.becomeCoachModalContainer}
        isOpen={isBecomeCoachModalOpen}
        onClose={() => setIsBecomeCoachModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.becomeCoachModalContent}>
          <h2 className={css.becomeCoachModalTitle}>Become a Coach</h2>
          <p className={css.becomeCoachModalText}>
            Are you sure you want to become a coach on Coachify? You will be able to list your services and accept bookings.
          </p>
          <div className={css.becomeCoachModalActions}>
            <PrimaryButton
              onClick={() => {
                setIsBecomeCoachModalOpen(false);
                // isAuthenticated only reflects the Sharetribe cookie, which
                // native sessions never carry -- this sent every logged-in
                // native user to /signup instead of the real coach-onboarding
                // flow below. currentUser is populated for both auth paths.
                if (!currentUser) {
                  const signupPath = pathByRouteName('SignupPage', routeConfiguration);
                  history.push(signupPath);
                } else if (currentUser?.attributes?.profile?.publicData?.coach) {
                  const dashboardPath = pathByRouteName('ProviderPage', routeConfiguration);
                  history.push(dashboardPath);
                } else if (stripeAccount?.id) {
                  onBecomeCoach().then(() => {
                    const dashboardPath = pathByRouteName('ProviderPage', routeConfiguration);
                    history.push(dashboardPath);
                  });
                } else {
                  const stripePayoutPath = pathByRouteName('StripePayoutPage', routeConfiguration);
                  history.push(stripePayoutPath);
                }
              }}
            >
              Agree & Continue
            </PrimaryButton>
            <Button
              rootClassName={css.becomeCoachModalCancel}
              onClick={() => setIsBecomeCoachModalOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        id="PayoutReconnectModal"
        containerClassName={css.becomeCoachModalContainer}
        isOpen={showPayoutReconnectModal}
        onClose={dismissPayoutReconnectModal}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.becomeCoachModalContent}>
          <h2 className={css.becomeCoachModalTitle}>Action needed: reconnect your payout account</h2>
          <p className={css.becomeCoachModalText}>
            We upgraded how coaches get paid on Coachify. Your previous Stripe
            connection doesn't carry over to the new system, so bookings,
            subscriptions, and donations can't pay out to you until you
            reconnect. This takes about a minute and only needs to be done once.
          </p>
          {payoutConnectError ? (
            <p className={css.becomeCoachModalText}>{payoutConnectError}</p>
          ) : null}
          <div className={css.becomeCoachModalActions}>
            <PrimaryButton onClick={handleConnectPayout} inProgress={payoutConnecting} disabled={payoutConnecting}>
              Reconnect Stripe
            </PrimaryButton>
            <Button
              rootClassName={css.becomeCoachModalCancel}
              onClick={dismissPayoutReconnectModal}
            >
              Remind me later
            </Button>
          </div>
        </div>
      </Modal>

      <GenericError show={showGenericError} />
    </div>
  );
};

/**
 * Topbar containing logo, main search and navigation links.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.desktopClassName add more style rules for TopbarDesktop
 * @param {Object} props.mobileRootClassName overwrite mobile layout root classes
 * @param {Object} props.mobileClassName add more style rules for mobile layout
 * @param {boolean} props.isAuthenticated
 * @param {boolean} props.isLoggedInAs
 * @param {Object} props.currentUser
 * @param {boolean} props.currentUserHasListings
 * @param {boolean} props.currentUserHasOrders
 * @param {string} props.currentPage
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onManageDisableScrolling
 * @param {Function} props.onResendVerificationEmail
 * @param {Object} props.sendVerificationEmailInProgress
 * @param {Object} props.sendVerificationEmailError
 * @param {boolean} props.showGenericError
 * @param {Object} props.history
 * @param {Function} props.history.push
 * @param {Object} props.location
 * @param {string} props.location.search '?foo=bar'
 * @returns {JSX.Element} topbar component
 */
const Topbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...props}
    />
  );
};

export default Topbar;
